(() => {
  'use strict';
  if (window.RudcaPhotoPages && window.RudcaCatalogData) return;
  const SIZE = 24, WINDOW = 3, CACHE = 3, IMAGE_RADIUS = 12, CONTROL_RADIUS = 3, CONTROL_CONCURRENCY = 2, REQUEST_TIMEOUT = 15000, CATALOG_LIMIT = 25000;
  const THEME = '188655665378';
  const browserFetch = (...args) => window.fetch(...args);
  const failure = (code, stage, message, status) => Object.assign(new Error(message), {photoCode:code, photoStage:stage, photoStatus:status});
  function sectionURL(value, preview) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) throw failure('CONTEXT_ORIGIN','startup','Invalid section origin');
    if (preview === THEME) {
      const current = new URL(location.href);
      url.searchParams.set('preview_theme_id', THEME);
      for (const key of ['preview_key','_fd','pb','locale']) {
        if (current.searchParams.has(key)) url.searchParams.set(key,current.searchParams.get(key));
      }
    }
    return url;
  }
  async function request(fetcher, url, options, stage, format) {
    try { return await requestOnce(fetcher,url,options,stage,format); }
    catch (error) {
      const prefix = stage.toUpperCase();
      const transient = ['catalog','stock'].includes(stage) &&
        (error.photoCode === `${prefix}_TIMEOUT` || error.photoCode === `${prefix}_NETWORK` ||
          error.photoCode === `${prefix}_HTTP` && [502,503,504].includes(error.photoStatus));
      if (!transient) throw error;
      // One bounded retry of this same read; never advance the cursor or retry Add.
      await new Promise((resolve,reject) => {
        const signal = options.signal;
        const abort = () => { clearTimeout(timer); reject(new DOMException('Request aborted','AbortError')); };
        const timer = setTimeout(() => { signal?.removeEventListener('abort',abort); resolve(); },300);
        if (signal?.aborted) { abort(); return; }
        signal?.addEventListener('abort',abort,{once:true});
      });
      return requestOnce(fetcher,url,options,stage,format);
    }
  }
  async function requestOnce(fetcher, url, options, stage, format) {
    const target = new URL(url,location.origin);
    if (target.origin !== location.origin) throw failure('CONTEXT_ORIGIN',stage,'Invalid request origin');
    const controller = new AbortController(), outer = options.signal;
    if (outer?.aborted) throw new DOMException('Request aborted','AbortError');
    let timer, rejectAbort;
    const cancelled = new Promise((_,reject) => { rejectAbort = reject; });
    const abort = () => { controller.abort(); rejectAbort(new DOMException('Request aborted','AbortError')); };
    outer?.addEventListener('abort',abort,{once:true});
    const timeout = new Promise((_,reject) => {
      timer = setTimeout(() => { reject(failure(`${stage.toUpperCase()}_TIMEOUT`,stage,'Request timed out')); controller.abort(); },REQUEST_TIMEOUT);
    });
    const work = (async () => {
      const response = await fetcher(url,{priority:'low',...options,signal:controller.signal});
      if (!response.ok) throw failure(`${stage.toUpperCase()}_HTTP`,stage,stage === 'catalog' ? 'Catalog request failed' : stage === 'stock' ? 'Physical-stock confirmation unavailable' : 'Product control unavailable',response.status);
      if (response.url && new URL(response.url,location.origin).origin !== location.origin) throw failure('CONTEXT_ORIGIN',stage,'Unexpected response origin');
      try { return await response[format](); }
      catch (error) {
        if (error.name === 'AbortError') throw error;
        throw failure(`${stage.toUpperCase()}_BODY`,stage,'Invalid response body',response.status);
      }
    })();
    try { return await Promise.race([work,timeout,cancelled]); }
    catch (error) {
      if (error.name === 'AbortError' || error.photoCode) throw error;
      throw failure(`${stage.toUpperCase()}_NETWORK`,stage,'Request unavailable');
    } finally {
      clearTimeout(timer); outer?.removeEventListener('abort',abort);
    }
  }
  const QUERY = `query PhotoCatalog($first:Int,$last:Int,$after:String,$before:String,$country:CountryCode!,$language:LanguageCode!){
    products(first:$first,last:$last,after:$after,before:$before,sortKey:ID,query:"available_for_sale:true"){
      nodes{id handle title vendor productType availableForSale onlineStoreUrl featuredImage{url(transform:{maxWidth:400}) width height} priceRange{minVariantPrice{amount currencyCode} maxVariantPrice{amount currencyCode}}}
      pageInfo{hasNextPage hasPreviousPage startCursor endCursor}
    }
  }`;
  const CONTEXT_QUERY = QUERY.replace('){\n    products', ') @inContext(country:$country,language:$language){\n    products');
  function interleave(products) {
    const key = (value) => typeof value === 'string' ? value.trim().toLowerCase() : '';
    const compareId = (a,b) => a.id.length - b.id.length || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    const pending = products.slice().sort(compareId);
    const vendors = new Map(), types = new Map();
    pending.forEach((product) => {
      const vendor = key(product.vendor), type = key(product.productType);
      vendors.set(vendor,(vendors.get(vendor) || 0) + 1);
      types.set(type,(types.get(type) || 0) + 1);
    });
    const result = [];
    let lastVendor = null, lastType = null;
    while (pending.length) {
      let best = 0, bestRank = null;
      pending.forEach((product,index) => {
        const vendor = key(product.vendor), type = key(product.productType);
        const rank = [vendor && vendor === lastVendor ? 1 : 0,-vendors.get(vendor),type && type === lastType ? 1 : 0,-types.get(type)];
        let order = 0;
        if (bestRank) {
          for (let field = 0; field < rank.length; field++) {
            if (rank[field] !== bestRank[field]) { order = rank[field] - bestRank[field]; break; }
          }
        }
        if (!bestRank || order < 0) {
          best = index; bestRank = rank;
        }
      });
      const product = pending.splice(best,1)[0];
      lastVendor = key(product.vendor); lastType = key(product.productType);
      vendors.set(lastVendor,vendors.get(lastVendor) - 1);
      types.set(lastType,types.get(lastType) - 1);
      result.push(product);
    }
    return result;
  }
  function productOrigin(html) {
    if (typeof html !== 'string' || html.length > 4096 || !html.trim()) return null;
    const parsed = new DOMParser().parseFromString(html,'text/html');
    const source = parsed.body.firstElementChild;
    if (parsed.body.children.length !== 1 || !source || !['A','SPAN'].includes(source.tagName) || !source.classList.contains('rudca-card-origin')) return null;
    const provenance = source.dataset.rudcaFlagSource;
    const label = source.getAttribute('aria-label')?.trim();
    const symbol = source.firstElementChild;
    if (!['selection','origin'].includes(provenance) || !label || label.length > 240 || source.children.length !== 1 || symbol?.tagName !== 'SPAN' || symbol.children.length || symbol.getAttribute('aria-hidden') !== 'true') return null;
    const flag = symbol.textContent.trim();
    if (!/^[\u{1F1E6}-\u{1F1FF}]{2}$/u.test(flag) || source.textContent.trim() !== flag) return null;
    const node = document.createElement(source.tagName === 'A' ? 'a' : 'span');
    if (source.tagName === 'A') {
      const href = source.getAttribute('href') || '';
      if (!href.startsWith('/') || href.startsWith('//')) return null;
      const url = new URL(href,location.origin);
      if (url.origin !== location.origin || url.search || url.hash || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[a-z0-9-]+(?:\/[a-z0-9-]+)?\/?$/i.test(url.pathname)) return null;
      node.href = url.href;
    } else {
      if (provenance !== 'origin' || source.getAttribute('role') !== 'img') return null;
      node.setAttribute('role','img');
    }
    node.className = 'rudca-photo-scroll__origin'; node.dataset.rudcaFlagSource = provenance;
    node.setAttribute('aria-label',label); node.title = label;
    const icon = document.createElement('span'); icon.setAttribute('aria-hidden','true'); icon.textContent = flag;
    node.appendChild(icon);
    return node;
  }
  class CatalogData {
    constructor(options) {
      this.fetch = options.fetch || browserFetch;
      this.signal = options.signal;
      this.country = options.country;
      this.language = options.language;
      this.root = (options.root || '/').replace(/\/?$/, '/');
      this.preview = options.preview === '188655665378' ? options.preview : null;
      this.cache = new Map();
      this.jobs = new Map();
    }
    async page(position = {}) {
      const direction = position.before ? -1 : 1;
      const key = JSON.stringify([direction, position.before || position.after || null]);
      if (this.cache.has(key)) {
        const result = this.cache.get(key); this.cache.delete(key); this.cache.set(key, result); return result;
      }
      if (this.jobs.has(key)) return this.jobs.get(key);
      const job = this.read(position).then((result) => {
        this.cache.set(key, result);
        while (this.cache.size > CACHE) this.cache.delete(this.cache.keys().next().value);
        return result;
      }).finally(() => this.jobs.delete(key));
      this.jobs.set(key, job);
      return job;
    }
    async read(position) {
      const reverse = Boolean(position.before);
      const variables = {first: reverse ? null : SIZE, last: reverse ? SIZE : null, after: position.after || null, before: position.before || null, country:this.country, language:this.language};
      const body = await request(this.fetch, '/api/2026-10/graphql.json', {method:'POST', credentials:'same-origin', signal:this.signal, headers:{'Content-Type':'application/json', Accept:'application/json'}, body:JSON.stringify({query:CONTEXT_QUERY, variables})},'catalog','json');
      if (body?.errors?.length || !body?.data?.products) throw failure('CATALOG_GRAPHQL','catalog','Catalog unavailable');
      const data = body.data.products, info = data.pageInfo;
      if (!Array.isArray(data.nodes) || data.nodes.length > SIZE || !info || typeof info.hasNextPage !== 'boolean' || typeof info.hasPreviousPage !== 'boolean') throw failure('CATALOG_SHAPE','catalog','Invalid catalog response');
      const hasNext = info.hasNextPage || Boolean(position.before), hasPrevious = info.hasPreviousPage || Boolean(position.after);
      if (hasNext && !info.endCursor || hasPrevious && !info.startCursor || position.after && info.endCursor === position.after || position.before && info.startCursor === position.before) throw failure('CATALOG_CURSOR','catalog','Catalog cursor did not advance');
      const ids = new Set();
      const products = data.nodes.filter((product) => product.availableForSale && product.onlineStoreUrl && product.featuredImage).map((product) => {
        const id = /^gid:\/\/shopify\/Product\/(\d+)$/.exec(product.id || '')?.[1];
        if (!id || ids.has(id) || !product.handle || typeof product.title !== 'string' || !product.priceRange?.minVariantPrice || !product.priceRange?.maxVariantPrice) throw failure('CATALOG_IDENTITY','catalog','Invalid product identity');
        ids.add(id);
        return {...product, id};
      });
      const stock = await this.verifyStock(products);
      // Unknown is not sold out: retry this same cursor rather than silently omit it.
      if (stock.summary.unknown) throw failure('STOCK_UNCONFIRMED','stock','Physical stock could not be confirmed');
      return {position:position.before ? {before:position.before} : position.after ? {after:position.after} : {},count:data.nodes.length,products:interleave(stock.products), stockSummary:stock.summary, start:info.startCursor, end:info.endCursor, next:hasNext ? {after:info.endCursor} : null, previous:hasPrevious ? {before:info.startCursor} : null};
    }
    async verifyStock(products) {
      const summary = {checked:products.length,eligible:0,excluded:0,unknown:0,reasons:{}};
      if (!products.length) return {products:[],summary};
      const batches = [];
      for (let start = 0; start < products.length; start += 12) batches.push(products.slice(start,start + 12));
      const allowed = new Set(), origins = new Map(), controls = new Map();
      const count = (reason,unknown) => {
        summary[unknown ? 'unknown' : 'excluded']++;
        summary.reasons[reason] = (summary.reasons[reason] || 0) + 1;
      };
      await Promise.all(batches.map(async (batch) => {
        const query = encodeURIComponent(batch.map((product) => product.handle).join('|'));
        const url = sectionURL(`${this.root}search?section_id=rudca-photo-stock&q=${query}`,this.preview);
        const text = await request(this.fetch,url.href,{method:'GET',credentials:'same-origin',signal:this.signal,headers:{Accept:'text/html'}},'stock','text');
        if (text.length > 64000) throw failure('STOCK_SIZE','stock','Invalid physical-stock response size');
        const document = new DOMParser().parseFromString(text,'text/html');
        const script = document.querySelector('script[data-photo-stock-response]');
        if (!script) throw failure('STOCK_MARKER','stock','Missing physical-stock confirmation');
        let body;
        try { body = JSON.parse(script.textContent); } catch (_) { throw failure('STOCK_JSON','stock','Invalid physical-stock confirmation'); }
        if (String(body?.theme) !== THEME) throw failure('STOCK_THEME','stock','Invalid physical-stock response');
        if (!Array.isArray(body.products) || body.products.length > 12) throw failure('STOCK_SHAPE','stock','Invalid physical-stock response');
        const seen = new Set();
        for (const record of body.products) {
          if (!record || typeof record.handle !== 'string' || seen.has(record.handle)) throw failure('STOCK_DUPLICATE','stock','Duplicate physical-stock identity');
          seen.add(record.handle);
          if (!batch.some((product) => product.handle === record.handle)) throw failure('STOCK_IDENTITY','stock','Unexpected physical-stock identity');
        }
        for (const product of batch) {
          const record = body.products.find((item) => item.handle === product.handle);
          if (!record) { count('missing_response',true); continue; }
          if (record.resolvedHandle !== product.handle || String(record.id) !== product.id) { count('identity_unconfirmed',true); continue; }
          if (record.complete !== true || typeof record.eligible !== 'boolean') { count('incomplete_confirmation',true); continue; }
          if (record.eligible === true) {
            allowed.add(product.id); summary.eligible++;
            controls.set(product.id,{controlIdentity:record.controlIdentity,stockHTML:record.stockHTML,singlePack:record.singlePack === true});
            if (typeof record.originHTML === 'string' && record.originHTML.length <= 4096) origins.set(product.id,record.originHTML);
          }
          else count(typeof record.reason === 'string' ? record.reason : 'not_eligible',false);
        }
      }));
      return {products:products.filter((product) => allowed.has(product.id)).map((product) => ({...product,...controls.get(product.id),originHTML:origins.get(product.id) || ''})),summary};
    }
    clear() { this.cache.clear(); }
  }
  class PhotoPages {
    constructor(host) {
      this.host = host;
      this.pages = [];
      this.pending = null;
      this.inflight = null;
      this.failed = false;
      this.formJobs = new Map();
      this.stopped = false;
      this.imageJobs = new Map();
      this.imageBatch = Promise.resolve();
      this.evicted = false;
      this.compactCycle = false;
      this.catalogLength = null;
      this.terminalPosition = null;
    }
    async start(signal) {
      this.signal = signal;
      document.addEventListener('rudca:basket-state', () => this.syncKnownControls(), {signal});
      this.status('loading');
      this.data = new CatalogData({signal, country:this.host.dataset.photoCountry, language:this.host.dataset.photoLanguage,root:this.host.dataset.photoRoot,preview:this.host.dataset.photoPreview});
      let first = await this.data.page(), offset = 0;
      while (!first.products.length && first.next && !signal.aborted) {
        // An empty stock batch must not turn idle warmup into a catalog scan.
        if (this.host.photoWarmup) await this.host.photoDemand;
        if (signal.aborted || !this.host.isConnected) return false;
        offset += first.count;
        first = await this.data.page(first.next);
      }
      this.confirmEnd(first,offset);
      first = {...first,offset,cycle:0};
      await this.preload(first.products.slice(0, 8), true);
      if (signal.aborted || !this.host.isConnected) return false;
      this.mount(first, 1, true);
      this.status(first.products.length ? null : 'empty');
      this.observer = new MutationObserver(() => this.commit());
      this.observer.observe(this.host.querySelector('[data-photo-controls]'), {subtree:true, attributes:true, attributeFilter:['aria-busy']});
      document.addEventListener('rudca:cart-settled', () => this.commit(), {signal});
      this.host.querySelector('[data-photo-retry]')?.addEventListener('click', () => {
        this.failed = false;
        this.host.querySelector('[data-photo-retry]').hidden = true;
        this.position(); this.controls();
      }, {signal});
      return true;
    }
    stop() {
      this.stopped = true;
      this.formJobs.forEach((job) => job.controller.abort());
      this.observer?.disconnect(); this.data?.clear(); this.pages = []; this.pending = null;
      this.imageJobs.forEach((job) => job.cancel()); this.imageJobs.clear();
    }
    preload(products, initial = false) {
      const ticket = this.preloadTicket = (this.preloadTicket || 0) + 1;
      // Only the newest adjacent batch matters; mounted images keep their own loads.
      if (!initial) this.imageJobs.forEach((job) => job.cancel());
      let ready, failed;
      const result = new Promise((resolve,reject) => { ready = resolve; failed = reject; });
      // The initial view can mount early; later batches still share one eight-job cap.
      this.imageBatch = this.imageBatch.then(async () => {
        if (this.stopped || this.signal?.aborted || ticket !== this.preloadTicket) { ready(); return; }
        const jobs = products.slice(0, 8).map((product) => new Promise((resolve) => {
          const image = new Image();
          let settled = false, timer;
          const finish = (cancel) => {
            if (settled) return;
            settled = true; clearTimeout(timer);
            this.signal?.removeEventListener('abort',abort);
            if (cancel) image.removeAttribute('src');
            if (this.imageJobs.get(product.id) === job) this.imageJobs.delete(product.id);
            resolve();
          };
          const abort = () => finish(true), job = {image,cancel:abort};
          this.imageJobs.set(product.id,job);
          this.signal?.addEventListener('abort',abort,{once:true});
          timer = setTimeout(abort,8000);
          image.decoding = 'async'; image.fetchPriority = 'low'; image.src = product.featuredImage.url;
          try { image.decode().then(() => finish(false),() => finish(false)); }
          catch (_) { finish(true); }
        }));
        if (initial) {
          let timer;
          const timeout = new Promise((resolve) => { timer = setTimeout(resolve,1500); });
          Promise.race([Promise.all(jobs.slice(0,3)),timeout]).then(() => { clearTimeout(timer); ready(); });
        }
        await Promise.all(jobs);
        ready();
      }).catch(failed);
      return result;
    }
    intent(direction) {
      if (![1,-1].includes(direction) || this.stopped || this.signal?.aborted || !this.host.links?.length) return;
      const host = this.host, rail = host.querySelector('[data-photo-rail]');
      // A clamped rail has no scroll delta. Preserve the user's boundary gesture
      // so a backwards seam can load without inventing a product or cloning it.
      this.motionSample = {id:host.links[host.nearest()]?.dataset.photoProduct,left:rail.scrollLeft,at:performance.now(),direction,speed:0};
      this.position();
    }
    position() {
      const host = this.host;
      if (this.stopped || this.signal.aborted || !host.links?.length) return;
      const current = host.nearest(), now = performance.now(), previous = this.motionSample;
      // Pixel-only motion inside one product does not change either neighbourhood.
      // A new mounted window invalidates this even when the numeric index matches.
      if (this.positionLinks !== host.links || this.positionCurrent !== current) {
        this.positionLinks = host.links; this.positionCurrent = current;
        this.images();
        this.controls();
      }
      // Photos and nearby native forms can be ready before vertical arrival.
      // Reserve/adjacent pages remain demand-driven until the viewport margin.
      if (host.photoWarmup) return;
      const rail = host.querySelector('[data-photo-rail]');
      // Follow the same product across a window rebase; its new index is not a swipe.
      const previousIndex = previous ? host.links.findIndex((link) => link.dataset.photoProduct === previous.id) : current;
      const indexDelta = previousIndex < 0 ? 0 : current - previousIndex;
      // Browser snap/elastic settling can rebound by a fractional pixel at a
      // clamped edge. Match the rail's boundary tolerance so that noise cannot
      // reverse an explicit seam gesture or discard its pending catalog page.
      const max = Math.max(0,rail.scrollWidth - rail.clientWidth);
      const stableLeft = (left) => left <= 1.5 ? 0 : left >= max - 1.5 ? max : left;
      const scrollDelta = previous && Number.isFinite(previous.left) ? (stableLeft(rail.scrollLeft) - stableLeft(previous.left)) / Math.max(1,host.step || 72) : 0;
      const delta = indexDelta || scrollDelta;
      const travel = delta ? Math.sign(delta) : previous?.direction || 1;
      const speed = delta ? Math.abs(delta) / Math.max(16, now - previous.at) : (previous?.speed || 0) * .8;
      this.motionSample = {id:host.links[current]?.dataset.photoProduct,left:rail.scrollLeft,at:now,direction:travel,speed};
      // A reversed swipe must not stay blocked by a page that would drop its new selection.
      if (this.pending && this.pending.direction !== travel) this.pending = null;
      // A new gesture may reopen reserve slots for an already fetched page.
      if (this.pending) { this.commit(); return; }
      if (this.inflight || this.failed) return;
      // Start one full page ahead, with up to twelve extra photos for a fast swipe.
      const lead = Math.min(SIZE + IMAGE_RADIUS, SIZE + Math.ceil(speed * 900));
      if (this.compactCycle) {
        // A short desktop catalog can be fully or almost fully visible. Without
        // one item's overflow there is no stable anchor for a safe node rotation.
        const overflow = rail.scrollWidth - rail.clientWidth;
        if (overflow < Math.max(1,host.step || 72)) return;
        const atEdge = travel === 1 ? rail.scrollLeft >= overflow - 1.5 : rail.scrollLeft <= 1.5;
        const margin = Math.min(SIZE,Math.floor((host.links.length - 1) / 3));
        if (host.links.length > 1 && (atEdge || (travel === 1 ? current >= host.links.length - 1 - margin : current <= margin))) {
          this.pending = {direction:travel,rotate:true};
          if (!this.commit()) host.scheduleSettle?.();
        }
        return;
      }
      const direction = travel === 1 && host.links.length - 1 - current <= lead ? 1 : travel === -1 && current <= lead ? -1 : 0;
      const navigation = direction && this.navigation(direction);
      if (!navigation) return;
      this.status('loading');
      this.inflight = this.adjacent(navigation,direction).then((page) => {
        if (this.stopped || this.signal.aborted || !host.isConnected || this.motionSample?.direction !== direction) return;
        if (!page) { this.enableCompactCycle(); return; }
        const ahead = direction === 1 ? page.products.slice(0, 8) : page.products.slice(-8);
        // Stock is confirmed already. Decode warming must not hold the rail hostage.
        this.preload(ahead).catch((error) => { if (error.name !== 'AbortError' && !this.stopped && !this.signal.aborted) this.error(error); });
        this.pending = {page,direction};
        if (!this.commit()) host.scheduleSettle?.();
      }).catch((error) => {
        if (error.name !== 'AbortError') this.error(error);
      }).finally(() => {
        this.inflight = null;
        // mount() paints while this request is still inflight. Recheck once, not a poll.
        if (!this.stopped && !this.signal.aborted && host.isConnected) this.position();
      });
    }
    confirmEnd(page,offset) {
      if (page.next) return;
      // Shopify caps one connection at 25,000. A capped connection is not proof
      // that the whole catalog ended, so it must never trigger a false wrap.
      if (offset + page.count >= CATALOG_LIMIT) throw failure('CATALOG_LIMIT','catalog','Catalog traversal limit reached');
      this.catalogLength = offset + page.count;
      // Storefront rejects last without before. Keep the exact forward request
      // which proved the real terminal page, including stock-empty terminal pages.
      this.terminalPosition = {...page.position};
    }
    navigation(direction) {
      const edge = direction === 1 ? this.pages.at(-1) : this.pages[0];
      if (!edge) return null;
      const position = direction === 1 ? edge.next : edge.previous;
      if (position) return {position,cycle:edge.cycle,offset:direction === 1 ? edge.offset + edge.count : edge.offset};
      if (direction === -1 && !edge.cycle) return null;
      if (this.catalogLength == null) return null;
      // A 73–120-item catalog can fit in the temporary reserve. Wait for idle
      // trimming before fetching its overlapping first page again.
      if (!this.evicted && !this.pages[0].previous && !this.pages[0].cycle && this.pages.reduce((n,page) => n + page.items.length,0) > SIZE * WINDOW) return null;
      // Each wrap starts at a real connection boundary, never at an empty stock
      // batch or a failed request. Fetch fresh stock instead of replaying cache.
      this.data.clear();
      return {position:direction === 1 ? {} : {...this.terminalPosition},cycle:edge.cycle + direction,offset:direction === 1 ? 0 : this.catalogLength};
    }
    async adjacent(navigation,direction) {
      const known = new Set(this.pages.flatMap((entry) => entry.items.map((item) => item.id)));
      let position = navigation.position, boundary = navigation.offset;
      while (!this.signal.aborted) {
        const page = await this.data.page(position);
        const offset = direction === 1 ? boundary : boundary - page.count;
        if (direction === 1) this.confirmEnd(page,offset);
        if (page.products.some((product) => !known.has(product.id))) return {...page,offset,cycle:navigation.cycle};
        const next = direction === 1 ? page.next : page.previous;
        if (!next) {
          // Fold stock-empty terminal batches into the adjacent record without
          // adding an empty DOM page or prematurely evicting a usable form.
          const edge = direction === 1 ? this.pages.at(-1) : this.pages[0];
          if (edge.cycle !== navigation.cycle) throw failure('CATALOG_OVERLAP','catalog','Catalog cursor needs a fresh retry');
          if (direction === 1) { edge.next = null; edge.end = page.end; edge.count = offset + page.count - edge.offset; }
          else { edge.previous = null; edge.start = page.start; edge.count += edge.offset - offset; edge.offset = offset; }
          return null;
        }
        boundary = direction === 1 ? offset + page.count : offset;
        position = next;
      }
      throw new DOMException('Request aborted','AbortError');
    }
    enableCompactCycle() {
      if (this.compactCycle || this.evicted || !this.pages.length || this.pages.at(-1).next || this.catalogLength == null || this.pages.reduce((n,page) => n + page.items.length,0) > SIZE * WINDOW) return;
      // When the entire eligible catalog fits in the live window, recycle its
      // existing nodes. Never clone a form or introduce duplicate product IDs.
      const first = this.pages[0], last = this.pages.at(-1);
      this.pages = [{...first,end:last.end,next:null,previous:null,count:this.catalogLength - first.offset,items:this.pages.flatMap((page) => page.items)}];
      this.compactCycle = true;
    }
    pinned(item,selected) {
      return item.id === selected || item.element.contains(document.activeElement) || item.card.contains(document.activeElement) || item.card.querySelector('form[aria-busy="true"]') || this.host.pendingAdds?.has(item.id);
    }
    rotate(direction) {
      const host = this.host, page = this.pages[0], items = page.items, current = host.nearest();
      const target = direction === 1 ? Math.floor((items.length - 1) / 2) : Math.ceil((items.length - 1) / 2);
      const count = direction === 1 ? current - target : target - current;
      if (count <= 0) { this.pending = null; return false; }
      const moved = direction === 1 ? items.slice(0,count) : items.slice(-count);
      const selected = host.links[current];
      if (moved.some((item) => this.pinned(item,selected?.dataset.photoProduct))) return false;
      host.lock();
      const rail = host.querySelector('[data-photo-rail]'), fragment = document.createDocumentFragment();
      const oldLeft = rail.scrollLeft, anchorLeft = this.anchor(selected,rail);
      moved.forEach((item) => fragment.appendChild(item.element));
      if (direction === 1) { rail.append(fragment); page.items = [...items.slice(count),...moved]; }
      else { rail.prepend(fragment); page.items = [...moved,...items.slice(0,-count)]; }
      host.links = [...rail.querySelectorAll('[data-photo-link]')];
      host.controlCards = [...host.querySelectorAll('[data-photo-control]')];
      host.measure();
      rail.scrollLeft = oldLeft + this.anchor(selected,rail) - anchorLeft;
      host.current = host.links.indexOf(selected); host.bound = undefined;
      if (this.motionSample) this.motionSample = {...this.motionSample,id:selected.dataset.photoProduct,left:rail.scrollLeft,at:performance.now()};
      this.pending = null;
      host.paint(); host.scheduleSettle();
      return true;
    }
    status(state) {
      if (this.failed && state !== 'error') return;
      // Adjacent-page work is silent once there is a usable photo rail.
      if (state === 'loading' && this.host.links?.length) state = null;
      this.host.dataset.photoLoadState = state || 'ready';
      if (state === 'loading' || !state) {
        delete this.host.dataset.photoErrorCode; delete this.host.dataset.photoErrorStage; delete this.host.dataset.photoErrorStatus;
        const diagnostic = this.host.querySelector('[data-photo-error-code]');
        if (diagnostic) { diagnostic.textContent = ''; diagnostic.hidden = true; }
      }
      const node = this.host.querySelector('[data-photo-loading-status]');
      if (!node) return;
      const key = state ? `photo${state[0].toUpperCase()}${state.slice(1)}` : '';
      node.textContent = key ? this.host.dataset[key] || '' : '';
      node.hidden = !node.textContent;
    }
    report(error, target = this.host) {
      const code = /^[A-Z_]{3,48}$/.test(error?.photoCode || '') ? error.photoCode : 'STARTUP_UNKNOWN';
      const stage = ['catalog','stock','control','startup'].includes(error?.photoStage) ? error.photoStage : 'startup';
      const status = Number.isInteger(error?.photoStatus) && error.photoStatus >= 100 && error.photoStatus <= 599 ? error.photoStatus : null;
      const prefix = target === this.host ? 'photoError' : 'photoControlError';
      target.dataset[`${prefix}Code`] = code; target.dataset[`${prefix}Stage`] = stage;
      if (status) target.dataset[`${prefix}Status`] = String(status); else delete target.dataset[`${prefix}Status`];
      const diagnostic = target.querySelector(target === this.host ? '[data-photo-error-code]' : '[data-photo-control-error-code]');
      if (diagnostic) { diagnostic.textContent = status ? `${code} (${status})` : code; diagnostic.hidden = false; }
      console.warn('[Rudca photo picker]',{code,stage,status,...(target === this.host ? {} : {product:target.dataset.photoControl})});
    }
    error(error) {
      this.host.cancelPendingAdd?.();
      if (error) this.report(error);
      this.failed = true;
      this.status('error');
      const retry = this.host.querySelector('[data-photo-retry]');
      if (retry) retry.hidden = false;
    }
    anchor(link,rail) { return link.getBoundingClientRect().left + rail.scrollLeft; }
    trackCalm() { return Boolean(this.host.trackSettled && !this.host.pointer && !this.host.touchActive); }
    canDrop(page,selected,direction) {
      const rail = this.host.querySelector('[data-photo-rail]').getBoundingClientRect();
      return page && page.items.every(item => {
        if (this.pinned(item,selected)) return false;
        const box = item.element.getBoundingClientRect();
        return direction === 1 ? box.right <= rail.left : box.left >= rail.right;
      });
    }
    trimReserve() {
      const host = this.host;
      if (this.trimming || this.stopped || this.signal.aborted || !host.isConnected || !this.trackCalm() || this.pages.length <= WINDOW) return false;
      const rail = host.querySelector('[data-photo-rail]'), selected = host.links[host.nearest()];
      const oldLeft = rail.scrollLeft, anchorLeft = this.anchor(selected,rail);
      let changed = false;
      this.trimming = true;
      while (this.pages.length > WINDOW) {
        const forward = this.motionSample?.direction !== -1;
        let direction = forward ? 1 : -1;
        let page = direction === 1 ? this.pages[0] : this.pages.at(-1);
        if (!this.canDrop(page,selected.dataset.photoProduct,direction)) {
          direction *= -1; page = direction === 1 ? this.pages[0] : this.pages.at(-1);
          if (!this.canDrop(page,selected.dataset.photoProduct,direction)) break;
        }
        if (!changed) host.lock();
        changed = true; this.evicted = true;
        page.items.forEach(item => { const job = this.formJobs.get(item.id); if (job?.item === item) job.controller.abort(); item.element.remove(); });
        this.pages.splice(this.pages.indexOf(page),1);
      }
      if (changed) {
        host.links = [...rail.querySelectorAll('[data-photo-link]')];
        host.controlCards = [...host.querySelectorAll('[data-photo-control]')];
        host.measure();
        rail.scrollLeft = oldLeft + this.anchor(selected,rail) - anchorLeft;
        host.current = host.links.indexOf(selected); host.bound = undefined;
        if (this.motionSample) this.motionSample = {...this.motionSample,id:selected.dataset.photoProduct,left:rail.scrollLeft,at:performance.now()};
        host.paint(); host.scheduleSettle(); host.scheduleTrackCalm?.();
      }
      this.trimming = false;
      return changed;
    }
    commit() {
      const host = this.host;
      this.trimReserve();
      if (!this.pending || this.signal.aborted || !host.isConnected) return;
      const {page,direction,rotate} = this.pending;
      if (rotate) {
        if (!this.trackCalm()) return;
        return this.rotate(direction);
      }
      // A native fling may append two real pages, never an unloaded spacer.
      const limit = host.reserveActive ? WINDOW + 2 : WINDOW;
      const appendOnly = direction === 1 && this.pages.length < limit;
      if (!appendOnly && !this.trackCalm()) return;
      const selected = host.links[host.nearest()]?.dataset.photoProduct;
      let drop = null;
      if (!appendOnly && this.pages.length >= WINDOW) {
        drop = direction === 1 ? this.pages[0] : this.pages.at(-1);
        if (!this.canDrop(drop,selected,direction)) return;
      }
      this.pending = null;
      this.mount(page,direction,false,drop);
      this.status(null);
      return true;
    }
    item(product) {
      const host = this.host, id = product.id;
      const root = (host.dataset.photoRoot || '/').replace(/\/?$/, '/');
      const url = new URL(`${root}products/${encodeURIComponent(product.handle)}`, location.origin);
      const element = document.createElement('div'); element.dataset.photoItem = id;
      const link = document.createElement('a'); link.dataset.photoLink = ''; link.dataset.photoProduct = id; link.href = url;
      const min = product.priceRange.minVariantPrice, max = product.priceRange.maxVariantPrice;
      const format = (money) => `${new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:money.currencyCode}).format(Number(money.amount))} ${money.currencyCode}`;
      const price = document.createElement('span'); price.className = 'rudca-photo-scroll__price';
      price.textContent = min.amount === max.amount ? format(min) : `${format(min)}–${format(max)}`;
      link.setAttribute('aria-label', `${product.title}, ${price.textContent}`);
      const image = document.createElement('img'); image.dataset.photoSrc = product.featuredImage.url; image.alt = ''; image.width = product.featuredImage.width; image.height = product.featuredImage.height; image.loading = 'eager'; image.decoding = 'async'; image.fetchPriority = 'low'; image.draggable = false;
      const media = document.createElement('span'); media.className = 'rudca-photo-scroll__media';
      media.appendChild(image); link.appendChild(media);
      const details = document.createElement('div'); details.className = 'rudca-photo-scroll__details'; details.appendChild(price);
      const origin = productOrigin(product.originHTML);
      if (origin) details.appendChild(origin);
      element.append(link,details);
      image.addEventListener('error', () => {
        if (!image.hasAttribute('src')) return;
        image.hidden = true;
        if (!link.querySelector('[data-photo-image-fallback]')) {
          const fallback = document.createElement('span'); fallback.dataset.photoImageFallback = '';
          fallback.textContent = product.title;
          Object.assign(fallback.style, {display:'flex',alignItems:'center',justifyContent:'center',height:'var(--photo-stage-height)',padding:'8px',textAlign:'center',fontSize:'13px'});
          media.appendChild(fallback);
        }
        host.scheduleSettle?.();
      });
      image.addEventListener('load', () => { image.hidden = false; link.querySelector('[data-photo-image-fallback]')?.remove(); host.scheduleSettle?.(); });
      const card = document.createElement('div'); card.className = 'product-item rudca-photo-control'; card.dataset.photoControl = id; card.dataset.photoControlLoading = ''; card.inert = false;
      const shell = host.querySelector('[data-photo-placeholder-template]')?.content.firstElementChild?.cloneNode(true);
      if (shell) {
        // A cold product has no confirmed basket identity yet. Keep the bright
        // footprint and explicit loading state; a click queues only this product.
        const button = shell.querySelector('button');
        const label = host.dataset.photoSelectionLoading || 'Loading options';
        button.dataset.photoPlaceholderAdd = ''; button.setAttribute('aria-busy','true');
        // Preserve the basket icon while loading; status and aria-busy explain readiness.
        button.setAttribute('aria-label',[button.getAttribute('aria-label'),label].filter(Boolean).join('. '));
        card.appendChild(shell);
        const status = document.createElement('p'); status.dataset.photoColdStatus = '';
        status.setAttribute('role','status'); status.textContent = label; card.appendChild(status);
      }
      element.appendChild(card);
      return {id,product,element,link,price,card,url:url.href};
    }
    mount(page,direction,initial,drop) {
      const host = this.host, rail = host.querySelector('[data-photo-rail]'), controls = host.querySelector('[data-photo-controls]');
      const selected = !initial && host.links[host.nearest()];
      const oldLeft = rail.scrollLeft, anchorLeft = selected ? this.anchor(selected,rail) : 0;
      const appendOnly = !initial && direction === 1 && !drop;
      if (!initial && !appendOnly) host.lock();
      const known = new Set(this.pages.flatMap((entry) => entry.items.map((item) => item.id)));
      const items = page.products.filter((product) => !known.has(product.id)).map((product) => this.item(product));
      const links = document.createDocumentFragment(), cards = document.createDocumentFragment();
      items.forEach((item) => { links.appendChild(item.element); });
      if (initial) rail.replaceChildren(links);
      else if (direction === -1) rail.prepend(links); else rail.append(links);
      if (drop) {
        this.evicted = true;
        drop.items.forEach((item) => {
          const job = this.formJobs.get(item.id);
          if (job?.item === item) job.controller.abort();
          item.element.remove(); item.card.remove();
        });
        this.pages.splice(this.pages.indexOf(drop),1);
      }
      const record = {start:page.start,end:page.end,next:page.next,previous:page.previous,count:page.count,offset:page.offset,cycle:page.cycle,items};
      if (page.stockSummary) host.dataset.photoStockSummary = JSON.stringify(page.stockSummary);
      if (direction === -1) this.pages.unshift(record); else this.pages.push(record);
      this.enableCompactCycle();
      host.links = [...rail.querySelectorAll('[data-photo-link]')];
      if (host.links.length) host.removeAttribute('data-photo-empty-shell');
      else if (initial) host.setAttribute('data-photo-empty-shell', '');
      host.controlCards = [...controls.querySelectorAll('[data-photo-control]')];
      this.syncKnownControls();
      if (!initial) {
        host.measure();
        // Do not write scrollLeft during append: even an equal value can stop momentum.
        if (!appendOnly) {
          if (selected?.isConnected) rail.scrollLeft = oldLeft + this.anchor(selected,rail) - anchorLeft;
          host.current = host.links.indexOf(selected); host.bound = undefined;
        }
        // Rebase the physical scroll baseline before paint observes this DOM change.
        if (this.motionSample) this.motionSample = {...this.motionSample,id:host.links[host.nearest()]?.dataset.photoProduct,left:rail.scrollLeft,at:performance.now()};
        host.paint(); host.scheduleSettle(); host.scheduleTrackCalm?.();
      }
    }
    imageReady(link) {
      const image = link?.querySelector('img');
      const fallback = link?.querySelector('[data-photo-image-fallback]');
      return Boolean(image && !image.hidden && image.complete && image.naturalWidth > 0 || fallback && !fallback.hidden && fallback.textContent.trim());
    }
    images() {
      const host = this.host, current = host.nearest();
      host.links.forEach((link,index) => {
        const image = link.querySelector('img[data-photo-src]');
        if (Math.abs(index - current) <= IMAGE_RADIUS) {
          if (!image.hasAttribute('src')) image.src = image.dataset.photoSrc;
        } else {
          if (image.hasAttribute('src')) image.removeAttribute('src');
          if (image.hasAttribute('srcset')) image.removeAttribute('srcset');
        }
      });
    }
    syncKnownControls() {
      if (this.syncingKnown || this.stopped || this.signal?.aborted || !this.host.isConnected || !window.RudcaPhotoBasket) return;
      this.syncingKnown = true;
      const mounted = [];
      try {
        // This bounded mounted window shares the native ledger even off screen.
        // No catalog scan, new cart read, speculative identity, or extra form fetch.
        for (const item of this.pages.flatMap(page => page.items)) {
          const identity = item.product?.controlIdentity;
          if (!this.controlPending(item) || item.readyControl || this.controlPinned(item) || !identity || identity.product !== item.id) continue;
          const control = window.RudcaPhotoBasket.copyControl(identity);
          if (!control) continue;
          const form = control.querySelector('form');
          form.setAttribute('data-rudca-photo-pending',this.host.dataset.photoPendingLabel || 'Queued: __COUNT__');
          if (typeof item.product.stockHTML === 'string' && item.product.stockHTML.length <= 8192) {
            const stockTemplate = document.createElement('template'); stockTemplate.innerHTML = item.product.stockHTML;
            const stock = stockTemplate.content.firstElementChild;
            if (stockTemplate.content.children.length === 1 && stock?.matches('span[data-rudca-card-stock][data-rudca-photo-stock]') &&
                stock.dataset.rudcaStockVariant === identity.variant && Number(stock.dataset.rudcaStockMax) === identity.stockMax) control.appendChild(stock);
          }
          item.readyControl = {result:{control,price:item.product.singlePack === true ? `Pack 1 · ${item.price.textContent.replace(/^Pack 1 · /,'')}` : undefined},started:performance.now()};
          this.applyControl(item, false);
          mounted.push(item.card);
        }
        if (mounted.length) {
          window.RudcaPhotoBasket.bind();
          if (this.host.mobileAdd) mounted.forEach(card => this.host.mobileAddIcons?.(card));
        }
      } finally { this.syncingKnown = false; }
    }
    controlPending(item, card = item?.card) {
      return Boolean(item && item.card === card && item.link.isConnected && card.isConnected &&
        card.hasAttribute('data-photo-control-loading') && !card.querySelector('form[aria-busy="true"]') &&
        this.pages.some((page) => page.items.includes(item)));
    }
    controlPinned(item) {
      const host = this.host;
      if (!host.pointer) return false;
      // Only the exact stationary pressed form needs to survive until click.
      // Neighbours can reveal their own confirmed basket state independently.
      if (!host.pointer.controlPress || host.locked || !host.validIntent?.(host.intent)) return true;
      return host.intent.card === item.card;
    }
    controls() {
      const host = this.host;
      if (this.stopped || this.signal.aborted || !host.isConnected || this.failed || !host.links?.length) return;
      const current = host.nearest(), items = this.pages.flatMap((page) => page.items);
      const byId = new Map(items.map((item) => [item.id,item]));
      const indexes = [current];
      for (let distance = 1; distance <= CONTROL_RADIUS; distance++) indexes.push(current + distance,current - distance);
      const wanted = indexes.map((index) => byId.get(host.links[index]?.dataset.photoProduct)).filter(Boolean);
      const wantedItems = new Set(wanted);
      wanted.forEach(item => {
        try { this.applyControl(item); }
        catch (error) { this.controlError(item,error); }
      });
      // A dropped page or a changed neighbourhood cannot retain a network slot.
      // Keep cancelled jobs counted until their promises settle: never exceed the cap.
      this.formJobs.forEach((job) => {
        if (!wantedItems.has(job.item) || !this.controlPending(job.item,job.card)) job.controller.abort();
      });
      const selected = byId.get(host.links[current]?.dataset.photoProduct);
      // If both slots are speculative, make room for the new current product.
      if (this.controlPending(selected) && !this.formJobs.has(selected.id) && this.formJobs.size >= CONTROL_CONCURRENCY) {
        const speculative = [...this.formJobs.values()].filter((job) => job.item !== selected && !job.controller.signal.aborted);
        speculative.sort((a,b) => wanted.indexOf(b.item) - wanted.indexOf(a.item));
        speculative[0]?.controller.abort();
      }
      for (const item of wanted) {
        if (this.formJobs.size >= CONTROL_CONCURRENCY) break;
        if (!this.controlPending(item) || item.readyControl || item.controlError || this.formJobs.has(item.id)) continue;
        const controller = new AbortController(), card = item.card;
        const abort = () => controller.abort();
        this.signal.addEventListener('abort',abort,{once:true});
        const job = {item,card,controller,started:performance.now()};
        this.formJobs.set(item.id,job);
        job.promise = this.fetchControl(item,controller.signal).then((result) => {
          if (this.stopped || this.signal.aborted || controller.signal.aborted || !host.isConnected || !this.controlPending(item,card)) return;
          item.readyControl = {result,started:job.started};
          this.applyControl(item);
        }).catch((error) => {
          if (error.name === 'AbortError' || this.stopped || this.signal.aborted || controller.signal.aborted || !this.controlPending(item,card)) return;
          this.controlError(item,error,card);
        }).finally(() => {
          this.signal.removeEventListener('abort',abort);
          if (this.formJobs.get(item.id) === job) this.formJobs.delete(item.id);
          this.controls();
        });
      }
    }
    controlError(item, error, card = item.card) {
      if (!this.controlPending(item,card)) return;
      const host = this.host;
      item.controlError = error; delete item.readyControl;
      host.cancelPendingAdd?.(item.id);
      // A failed purchase control cannot stop an already stock-verified catalog.
      // Remove its Add placeholder; retry is a read of this exact product only.
      card.removeAttribute('data-photo-control-loading');
      const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'button';
      retry.dataset.photoControlRetry = '';
      retry.textContent = host.querySelector('[data-photo-retry]')?.textContent.trim() || '';
      retry.setAttribute('aria-label',`${host.dataset.photoError || ''} ${retry.textContent}`.trim());
      const diagnostic = document.createElement('p'); diagnostic.dataset.photoColdStatus = ''; diagnostic.dataset.photoControlErrorCode = '';
      diagnostic.setAttribute('role','status');
      card.replaceChildren(retry,diagnostic);
      this.report(error,card);
      retry.addEventListener('click', () => {
        if (retry.disabled || this.stopped || this.signal.aborted || this.failed || item.card !== card ||
          !item.controlError || !item.link.isConnected || !card.isConnected ||
          card.closest('[data-photo-item]') !== item.element || card.dataset.photoControl !== item.id ||
          !this.pages.some((page) => page.items.includes(item))) return;
        host.cancelPendingAdd?.(item.id);
        delete item.controlError;
        card.setAttribute('data-photo-control-loading',''); retry.disabled = true;
        diagnostic.textContent = host.dataset.photoSelectionLoading || '';
        this.controls(); host.scheduleSettle();
      }, {signal:this.signal});
      host.scheduleSettle();
    }
    applyControl(item, bind = true) {
      const host = this.host, pending = item.readyControl;
      if (!pending || this.controlPinned(item) || !this.controlPending(item)) return;
      const {result,started} = pending;
      delete item.readyControl;
      // A real fetch already parsed this inert control while validating stock.
      // Retain that exact node until its card is safe to replace; do not parse again.
      let replacement = result.control;
      if (!replacement) {
        const template = document.createElement('template'); template.innerHTML = result.html;
        replacement = template.content.querySelector('[data-photo-control]');
      }
      if (result.controlConsumed || replacement?.dataset.photoControl !== item.id || replacement.isConnected) throw failure('CONTROL_IDENTITY','control','Product control identity mismatch');
      result.controlConsumed = true;
      replacement.dataset.photoNativeMs = String(Math.round(performance.now() - started));
      replacement.dataset.photoNativeReady = 'true';
      // Keep the cold Add surface available until binding AND selection settle.
      if (replacement.querySelector('form')) replacement.dataset.photoControlBinding = '';
      replacement.hidden = false; replacement.inert = !host.coldControl?.(replacement);
      if (host.pendingAdds?.has(item.id)) {
        replacement.dataset.photoAddPending = '';
        const status = document.createElement('p'); status.dataset.photoColdStatus=''; status.setAttribute('role','status');
        status.textContent = (host.dataset.photoPendingLabel || '').replace('__COUNT__',String(host.pendingAdds.get(item.id).count || 1)); replacement.appendChild(status);
      }
      item.card.replaceWith(replacement); item.card = replacement;
      // Bind synchronously to the latest authoritative + optimistic ledger before
      // this newly mounted form can paint. A response never carries cart quantity.
      if (bind) {
        window.RudcaPhotoBasket?.bind();
        if (host.mobileAdd) host.mobileAddIcons?.(replacement);
      }
      if (result.price) item.price.textContent = result.price;
      if (result.label) item.link.setAttribute('aria-label',result.label);
      host.controlCards = [...host.querySelectorAll('[data-photo-control]')];
      host.scheduleSettle();
    }
    async fetchControl(item, signal = this.signal) {
      const url = sectionURL(item.url,this.host.dataset.photoPreview); url.searchParams.set('section_id','rudca-photo-catalog');
      const text = await request(browserFetch,url,{credentials:'same-origin',signal,headers:{Accept:'text/html'}},'control','text');
      if (text.length > 50000) throw failure('CONTROL_SIZE','control','Product control exceeded size limit');
      const doc = new DOMParser().parseFromString(text,'text/html');
      const root = doc.querySelector('[data-photo-product-response]');
      if (root?.dataset.photoTheme !== '188655665378' || root?.dataset.photoProductResponse !== item.id) throw failure('CONTROL_IDENTITY','control','Product control response mismatch');
      const template = root.querySelector('template[data-photo-control-template]');
      if (!template) throw failure('CONTROL_MARKER','control','Missing product control');
      const form = template.content.querySelector('form');
      if (form && (root.dataset.photoEligible !== 'true' || root.dataset.photoSelectedEligible !== 'true')) throw failure('CONTROL_STOCK','control','Selected physical stock is not confirmed');
      const control = template.content.querySelector('[data-photo-control]');
      if (control?.dataset.photoControl !== item.id) throw failure('CONTROL_IDENTITY','control','Product control identity mismatch');
      return {control,price:root.dataset.photoPrice?.replace(/\s+/g,' ').trim(),label:root.dataset.photoLabel?.replace(/\s+/g,' ').trim()};
    }
  }
  window.RudcaCatalogData = CatalogData;
  window.RudcaPhotoPages = PhotoPages;
})();

